package com.example.borshi.web.nutritionist.repository

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.firebase.FirebaseFirestore
import com.example.borshi.web.nutritionist.models.*
import kotlinx.coroutines.await
import kotlin.js.Date
import kotlin.js.Promise

@NoLiveLiterals
class FirebaseNutritionistRepository(private val firebase: FirebaseService) : NutritionistRepository {

    // Helper to get Firestore instance - lazy initialization to ensure Firebase is initialized first
    private val db by lazy {
        firebase.init()
        FirebaseFirestore.getFirestore()
    }

    override suspend fun getNutritionistProfile(nutritionistId: String): Result<NutritionistProfile> = runCatching {
        try {
            val snapshot = (FirebaseFirestore.getDoc(
                FirebaseFirestore.doc(db, "nutritionist_profiles", nutritionistId)
            ) as Promise<*>).await()

            val doc = snapshot.asDynamic()
            if (!doc.exists()) {
                throw Exception("Nutritionist profile not found in nutritionist_profiles collection")
            }

            val data = doc.data()

            // Map Firebase fields to NutritionistProfile model
            // Firebase uses: experience_years, is_active, profile_image, total_clients, etc.
            NutritionistProfile(
                nutritionistId = data.nutritionistId as? String ?: nutritionistId,
                name = data.name as? String ?: "",
                credentials = data.qualifications as? String ?: "", // Firebase uses 'qualifications'
                specializations = listOf(data.specialization as? String ?: ""), // Firebase uses single 'specialization'
                experience = (data.experience_years as? Number)?.toInt() ?: 0, // Firebase uses 'experience_years'
                rating = (data.rating as? Number)?.toDouble() ?: 0.0,
                totalClients = (data.total_clients as? Number)?.toInt() ?: 0, // Firebase uses 'total_clients'
                activeClients = 0, // Not in Firebase, calculate if needed
                bio = data.bio as? String ?: "",
                profileImage = data.profile_image as? String ?: "", // Firebase uses 'profile_image'
                contactEmail = data.email as? String ?: "", // Firebase uses 'email'
                phone = data.phone as? String ?: "",
                isActive = data.is_active as? Boolean ?: true, // Firebase uses 'is_active'
                createdAt = (data.created_at as? Number)?.toLong() ?: 0, // Firebase uses 'created_at'
                updatedAt = (data.updated_at as? Number)?.toLong() ?: 0 // Firebase uses 'updated_at'
            )
        } catch (e: Exception) {
            console.error("Error fetching nutritionist profile:", e)
            console.error("Error message:", e.message)
            console.error("Error stack:", e.asDynamic().stack)
            throw e
        }
    }

    override suspend fun updateNutritionistProfile(profile: NutritionistProfile): Result<Unit> {
        return try {
            val data = js("({})")
            data.name = profile.name
            data.credentials = profile.credentials
            data.specializations = profile.specializations.toTypedArray()
            data.experience = profile.experience
            data.totalClients = profile.totalClients
            data.activeClients = profile.activeClients
            data.bio = profile.bio
            data.profileImage = profile.profileImage
            data.contactEmail = profile.contactEmail
            data.phone = profile.phone
            data.isActive = profile.isActive
            data.updatedAt = js("Date.now()")

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "nutritionists", profile.nutritionistId),
                data
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error updating nutritionist profile:", e)
            Result.failure(e)
        }
    }

    override suspend fun getDashboardStats(nutritionistId: String): Result<DashboardStats> = runCatching {
        try {
            // Fetch all patients for this nutritionist
            val usersQuery = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "users"),
                FirebaseFirestore.where("assignedNutritionistId", "==", nutritionistId)
            )

            val usersSnapshot = (FirebaseFirestore.getDocs(usersQuery) as Promise<*>).await()
            val usersDocs = usersSnapshot.asDynamic().docs as Array<*>

            val totalPatients = usersDocs.size
            var activePatients = 0
            var totalAdherence = 0
            var patientsWithAdherence = 0

            // Count active patients and calculate average adherence
            usersDocs.forEach { doc ->
                val data = doc.asDynamic().data()
                val status = data.status as? String ?: "ACTIVE"
                if (status == "ACTIVE") {
                    activePatients++
                }

                val adherence = (data.adherenceScore as? Number)?.toInt() ?: 0
                if (adherence > 0) {
                    totalAdherence += adherence
                    patientsWithAdherence++
                }
            }

            val averageAdherence = if (patientsWithAdherence > 0) {
                totalAdherence / patientsWithAdherence
            } else 0

            // Count plans created by this nutritionist
            val plansQuery = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "nutritionist_base_plans"),
                FirebaseFirestore.where("nutritionistId", "==", nutritionistId)
            )
            val plansSnapshot = (FirebaseFirestore.getDocs(plansQuery) as Promise<*>).await()
            val plansCreated = (plansSnapshot.asDynamic().docs as Array<*>).size

            // Count active alerts (patients with low adherence or needing review)
            var activeAlerts = 0
            usersDocs.forEach { doc ->
                val data = doc.asDynamic().data()
                val adherence = (data.adherenceScore as? Number)?.toInt() ?: 0
                val lastCheckIn = (data.lastCheckIn as? Number)?.toLong() ?: 0
                val now = js("Date.now()") as Long
                val daysSinceCheckIn = (now - lastCheckIn) / (1000 * 60 * 60 * 24)

                // Alert if adherence < 60% or no check-in for 7+ days
                if (adherence < 60 || daysSinceCheckIn > 7) {
                    activeAlerts++
                }
            }

            // Count pending reviews (patients with adjustmentsNeeded flag for this nutritionist)
            val progressQuery = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "patient_progress"),
                FirebaseFirestore.where("nutritionistId", "==", nutritionistId),
                FirebaseFirestore.where("adjustmentsNeeded", "==", true)
            )
            val progressSnapshot = (FirebaseFirestore.getDocs(progressQuery) as Promise<*>).await()
            val pendingReviews = (progressSnapshot.asDynamic().docs as Array<*>).size

            // Today's appointments - for now return 0, can be implemented later
            val todayAppointments = 0

            console.log("✅ Dashboard stats calculated - Patients: $totalPatients, Active: $activePatients, Plans: $plansCreated")

            DashboardStats(
                totalPatients = totalPatients,
                activePatients = activePatients,
                averageAdherence = averageAdherence,
                plansCreated = plansCreated,
                activeAlerts = activeAlerts,
                todayAppointments = todayAppointments,
                pendingReviews = pendingReviews
            )
        } catch (e: Exception) {
            console.error("❌ Error calculating dashboard stats:", e)
            // Return zeros on error instead of failing
            DashboardStats(
                totalPatients = 0,
                activePatients = 0,
                averageAdherence = 0,
                plansCreated = 0,
                activeAlerts = 0,
                todayAppointments = 0,
                pendingReviews = 0
            )
        }
    }

    override suspend fun getPatients(nutritionistId: String): Result<List<Patient>> {
        return try {
            val usersQuery = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "users"),
                FirebaseFirestore.where("assignedNutritionistId", "==", nutritionistId)
            )

            val snapshot = (FirebaseFirestore.getDocs(usersQuery) as Promise<*>).await()
            val patients = mutableListOf<Patient>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val patient = Patient(
                    patientId = docDynamic.id as String,
                    userId = data.userId as? String ?: "",
                    name = data.name as? String ?: "",
                    email = data.email as? String ?: "",
                    phone = data.phone as? String ?: "",
                    profileImage = data.profileImage as? String ?: "",
                    age = (data.age as? Number)?.toInt() ?: 0,
                    gender = data.gender as? String ?: "",
                    height = (data.height as? Number)?.toDouble() ?: 0.0,
                    weight = (data.weight as? Number)?.toDouble() ?: 0.0,
                    bmi = (data.bmi as? Number)?.toDouble() ?: 0.0,
                    healthGoals = (data.healthGoals as? Array<String>)?.toList() ?: emptyList(),
                    healthConditions = (data.healthConditions as? Array<String>)?.toList() ?: emptyList(),
                    allergies = (data.allergies as? Array<String>)?.toList() ?: emptyList(),
                    medications = (data.medications as? Array<String>)?.toList() ?: emptyList(),
                    dietaryRestrictions = (data.dietaryRestrictions as? Array<String>)?.toList() ?: emptyList(),
                    activityLevel = data.activityLevel as? String ?: "moderate",
                    foodPreferences = (data.foodPreferences as? Array<String>)?.toList() ?: emptyList(),
                    currentPlanId = data.currentPlanId as? String,
                    adherenceScore = (data.adherenceScore as? Number)?.toInt() ?: 0,
                    lastCheckIn = (data.lastCheckIn as? Number)?.toLong() ?: 0,
                    status = try {
                        PatientStatus.valueOf(data.status as? String ?: "ACTIVE")
                    } catch (e: Exception) { PatientStatus.ACTIVE },
                    assignedNutritionistId = data.assignedNutritionistId as? String ?: ""
                )
                patients.add(patient)
            }
            Result.success(patients)
        } catch (e: Exception) {
            console.error("Error fetching patients:", e)
            Result.failure(e)
        }
    }

    override suspend fun getPatient(patientId: String): Result<Patient> {
        return try {
            val snapshot = (FirebaseFirestore.getDoc(
                FirebaseFirestore.doc(db, "users", patientId)
            ) as Promise<*>).await()

            val doc = snapshot.asDynamic()
            if (!doc.exists()) {
                throw Exception("Patient not found")
            }

            val data = doc.data()
            val patient = Patient(
                patientId = doc.id as String,
                userId = data.userId as? String ?: "",
                name = data.name as? String ?: "",
                email = data.email as? String ?: "",
                phone = data.phone as? String ?: "",
                profileImage = data.profileImage as? String ?: "",
                age = (data.age as? Number)?.toInt() ?: 0,
                gender = data.gender as? String ?: "",
                height = (data.height as? Number)?.toDouble() ?: 0.0,
                weight = (data.weight as? Number)?.toDouble() ?: 0.0,
                bmi = (data.bmi as? Number)?.toDouble() ?: 0.0,
                healthGoals = (data.healthGoals as? Array<String>)?.toList() ?: emptyList(),
                healthConditions = (data.healthConditions as? Array<String>)?.toList() ?: emptyList(),
                allergies = (data.allergies as? Array<String>)?.toList() ?: emptyList(),
                medications = (data.medications as? Array<String>)?.toList() ?: emptyList(),
                dietaryRestrictions = (data.dietaryRestrictions as? Array<String>)?.toList() ?: emptyList(),
                activityLevel = data.activityLevel as? String ?: "moderate",
                foodPreferences = (data.foodPreferences as? Array<String>)?.toList() ?: emptyList(),
                currentPlanId = data.currentPlanId as? String,
                adherenceScore = (data.adherenceScore as? Number)?.toInt() ?: 0,
                lastCheckIn = (data.lastCheckIn as? Number)?.toLong() ?: 0,
                status = try {
                    PatientStatus.valueOf(data.status as? String ?: "ACTIVE")
                } catch (e: Exception) { PatientStatus.ACTIVE },
                assignedNutritionistId = data.assignedNutritionistId as? String ?: ""
            )
            Result.success(patient)
        } catch (e: Exception) {
            console.error("Error fetching patient:", e)
            Result.failure(e)
        }
    }

    override suspend fun createUser(patient: Patient): Result<String> {
        return try {
            // Generate a unique user ID with timestamp - use plain JS number
            val timestamp = js("Date.now()") as Double
            val userId = "user_${timestamp.toLong()}"

            val data = js("({})")
            data.userId = userId
            data.name = patient.name
            data.displayName = patient.name
            data.email = patient.email
            data.phone = patient.phone
            data.age = patient.age
            data.gender = patient.gender
            data.height = patient.height
            data.weight = patient.weight
            data.bmi = patient.bmi
            data.healthGoals = patient.healthGoals.toTypedArray()
            data.healthConditions = patient.healthConditions.toTypedArray()
            data.allergies = patient.allergies.toTypedArray()
            data.dietaryRestrictions = patient.dietaryRestrictions.toTypedArray()
            data.assignedNutritionistId = patient.assignedNutritionistId
            data.status = patient.status.name
            data.activityLevel = "moderate" // Default
            data.adherenceScore = 0
            data.alertsCount = 0
            data.createdAt = timestamp  // Plain JS number
            data.updatedAt = timestamp  // Plain JS number

            // Create document with custom ID
            (FirebaseFirestore.setDoc(
                FirebaseFirestore.doc(db, "users", userId),
                data
            ) as Promise<*>).await()

            console.log("✅ User created successfully:", userId)
            Result.success(userId)
        } catch (e: Exception) {
            console.error("❌ Error creating user:", e)
            Result.failure(e)
        }
    }

    override suspend fun updatePatient(patient: Patient): Result<Unit> {
        return try {
            val data = js("({})")
            data.name = patient.name
            data.displayName = patient.name
            data.email = patient.email
            data.phone = patient.phone
            data.age = patient.age
            data.gender = patient.gender
            data.height = patient.height
            data.weight = patient.weight
            data.bmi = patient.bmi
            data.healthConditions = patient.healthConditions.toTypedArray()
            data.allergies = patient.allergies.toTypedArray()
            data.medications = patient.medications.toTypedArray()
            data.dietaryRestrictions = patient.dietaryRestrictions.toTypedArray()
            data.healthGoals = patient.healthGoals.toTypedArray()
            data.activityLevel = patient.activityLevel
            data.foodPreferences = patient.foodPreferences.toTypedArray()
            data.adherenceScore = patient.adherenceScore
            data.status = patient.status.name
            data.assignedNutritionistId = patient.assignedNutritionistId
            data.updatedAt = js("Date.now()")

            // If patientId is empty, create new document
            if (patient.patientId.isEmpty()) {
                data.createdAt = js("Date.now()")
                data.role = "user"
                (FirebaseFirestore.addDoc(
                    FirebaseFirestore.collection(db, "users"),
                    data
                ) as Promise<*>).await()
            } else {
                (FirebaseFirestore.updateDoc(
                    FirebaseFirestore.doc(db, "users", patient.patientId),
                    data
                ) as Promise<*>).await()
            }

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error updating patient:", e)
            Result.failure(e)
        }
    }

    override suspend fun getPatientProgress(patientId: String, limit: Int): Result<List<PatientProgress>> {
        return try {
            val queryRef = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "patient_progress"),
                FirebaseFirestore.where("patientId", "==", patientId),
                FirebaseFirestore.orderBy("createdAt", "desc"),
                FirebaseFirestore.limit(limit)
            )

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val progressList = mutableListOf<PatientProgress>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val progress = PatientProgress(
                    progressId = docDynamic.id as String,
                    patientId = data.patientId as? String ?: "",
                    recordDate = (data.recordDate as? Number)?.toLong() ?: 0,
                    weight = (data.weight as? Number)?.toDouble() ?: 0.0,
                    bmi = (data.bmi as? Number)?.toDouble() ?: 0.0,
                    adherenceScore = (data.adherenceScore as? Number)?.toInt() ?: 0,
                    mood = data.mood as? String ?: "Neutral",
                    energyLevel = (data.energyLevel as? Number)?.toInt() ?: 5,
                    nutritionistNotes = data.nutritionistNotes as? String ?: "",
                    adjustmentsNeeded = data.adjustmentsNeeded as? Boolean ?: false,
                    mealsCompleted = (data.mealsCompleted as? Number)?.toInt() ?: 0,
                    mealsPlanned = (data.mealsPlanned as? Number)?.toInt() ?: 0,
                    notes = data.notes as? String ?: ""
                )
                progressList.add(progress)
            }
            Result.success(progressList)
        } catch (e: Exception) {
            console.error("Error fetching patient progress:", e)
            Result.failure(e)
        }
    }

    override suspend fun addPatientProgress(progress: PatientProgress): Result<Unit> {
        return try {
            val timestamp = js("Date.now()") as Double
            val data = js("({})")
            data.patientId = progress.patientId
            data.recordDate = if (progress.recordDate > 0) progress.recordDate else timestamp
            data.weight = progress.weight
            data.bmi = progress.bmi
            data.adherenceScore = progress.adherenceScore
            data.mood = progress.mood
            data.energyLevel = progress.energyLevel
            data.nutritionistNotes = progress.nutritionistNotes
            data.adjustmentsNeeded = progress.adjustmentsNeeded
            data.mealsCompleted = progress.mealsCompleted
            data.mealsPlanned = progress.mealsPlanned
            data.notes = progress.notes

            (FirebaseFirestore.addDoc(
                FirebaseFirestore.collection(db, "patient_progress"),
                data
            ) as Promise<*>).await()
            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error adding patient progress:", e)
            Result.failure(e)
        }
    }

    override suspend fun updatePatientProgress(progress: PatientProgress): Result<Unit> {
        return try {
            val updateData = js("({})")
            updateData.weight = progress.weight
            updateData.bmi = progress.bmi
            updateData.adherenceScore = progress.adherenceScore
            updateData.mealsCompleted = progress.mealsCompleted
            updateData.mealsPlanned = progress.mealsPlanned
            updateData.nutritionistNotes = progress.nutritionistNotes
            updateData.adjustmentsNeeded = progress.adjustmentsNeeded
            updateData.updatedAt = js("Date.now()")

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "patient_progress", progress.progressId),
                updateData
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error updating patient progress:", e)
            Result.failure(e)
        }
    }

    override suspend fun deletePatientProgress(progressId: String): Result<Unit> {
        return try {
            (FirebaseFirestore.deleteDoc(
                FirebaseFirestore.doc(db, "patient_progress", progressId)
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error deleting patient progress:", e)
            Result.failure(e)
        }
    }

    override suspend fun getAlerts(nutritionistId: String, unreadOnly: Boolean): Result<List<PatientAlert>> {
        // TODO: Implement alerts collection if needed
        return Result.success(emptyList())
    }

    override suspend fun markAlertAsRead(alertId: String): Result<Unit> {
        return Result.success(Unit)
    }

    override suspend fun getBasePlans(nutritionistId: String): Result<List<NutritionistBasePlan>> {
        return try {
            val queryRef = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "nutritionist_base_plans"),
                FirebaseFirestore.where("nutritionistId", "==", nutritionistId),
                FirebaseFirestore.where("isActive", "==", true)
            )

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val plans = mutableListOf<NutritionistBasePlan>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val targets = data.nutritionalTargets ?: js("({})")

                val plan = NutritionistBasePlan(
                    basePlanId = docDynamic.id as String,
                    nutritionistId = data.nutritionistId as? String ?: "",
                    nutritionistName = data.nutritionistName as? String ?: "",
                    planName = data.planName as? String ?: "",
                    category = try {
                        PlanCategory.valueOf(data.category as? String ?: "GENERAL_HEALTH")
                    } catch (e: Exception) { PlanCategory.GENERAL_HEALTH },
                    description = data.description as? String ?: "",
                    nutritionalTargets = NutritionalTargets(
                        proteinPerWeek = (targets.proteinPerWeek as? Number)?.toInt() ?: 0,
                        caloriesPerDay = (targets.caloriesPerDay as? Number)?.toInt() ?: 0,
                        omega3PerWeek = (targets.omega3PerWeek as? Number)?.toInt() ?: 0,
                        fatPerDay = (targets.fatPerDay as? Number)?.toInt() ?: 0,
                        carbsPerDay = (targets.carbsPerDay as? Number)?.toInt() ?: 0
                    ),
                    targetAudience = data.targetAudience as? String ?: "",
                    goals = (data.goals as? Array<String>)?.toList() ?: emptyList(),
                    imageUrl = data.imageUrl as? String ?: "",
                    isActive = data.isActive as? Boolean ?: true,
                    isPublic = data.isPublic as? Boolean ?: false
                )
                plans.add(plan)
            }
            Result.success(plans)
        } catch (e: Exception) {
            console.error("Error fetching base plans:", e)
            Result.failure(e)
        }
    }

    override suspend fun getBasePlan(planId: String): Result<NutritionistBasePlan> {
        return try {
            val docSnap = (FirebaseFirestore.getDoc(
                FirebaseFirestore.doc(db, "nutritionist_base_plans", planId)
            ) as Promise<*>).await()

            val docSnapDynamic = docSnap.asDynamic()
            if (!docSnapDynamic.exists()) throw Exception("Plan not found")

            val data = docSnapDynamic.data()
            val targets = data.nutritionalTargets ?: js("({})")

            val plan = NutritionistBasePlan(
                basePlanId = docSnapDynamic.id as String,
                nutritionistId = data.nutritionistId as? String ?: "",
                nutritionistName = data.nutritionistName as? String ?: "",
                planName = data.planName as? String ?: "",
                category = try {
                    PlanCategory.valueOf(data.category as? String ?: "GENERAL_HEALTH")
                } catch (e: Exception) { PlanCategory.GENERAL_HEALTH },
                description = data.description as? String ?: "",
                nutritionalTargets = NutritionalTargets(
                    proteinPerWeek = (targets.proteinPerWeek as? Number)?.toInt() ?: 0,
                    caloriesPerDay = (targets.caloriesPerDay as? Number)?.toInt() ?: 0,
                    omega3PerWeek = (targets.omega3PerWeek as? Number)?.toInt() ?: 0,
                    fatPerDay = (targets.fatPerDay as? Number)?.toInt() ?: 0,
                    carbsPerDay = (targets.carbsPerDay as? Number)?.toInt() ?: 0
                ),
                targetAudience = data.targetAudience as? String ?: "",
                goals = (data.goals as? Array<String>)?.toList() ?: emptyList(),
                imageUrl = data.imageUrl as? String ?: "",
                isActive = data.isActive as? Boolean ?: true,
                isPublic = data.isPublic as? Boolean ?: false
            )
            Result.success(plan)
        } catch (e: Exception) {
            console.error("Error fetching plan:", e)
            Result.failure(e)
        }
    }

    override suspend fun createBasePlan(plan: NutritionistBasePlan): Result<String> {
        return try {
            val data = js("({})")
            data.nutritionistId = plan.nutritionistId
            data.nutritionistName = plan.nutritionistName
            data.planName = plan.planName
            data.category = plan.category.name
            data.description = plan.description

            val targets = js("({})")
            targets.proteinPerWeek = plan.nutritionalTargets.proteinPerWeek
            targets.caloriesPerDay = plan.nutritionalTargets.caloriesPerDay
            targets.omega3PerWeek = plan.nutritionalTargets.omega3PerWeek
            targets.fatPerDay = plan.nutritionalTargets.fatPerDay
            targets.carbsPerDay = plan.nutritionalTargets.carbsPerDay
            data.nutritionalTargets = targets

            data.targetAudience = plan.targetAudience
            data.goals = plan.goals.toTypedArray()
            data.imageUrl = plan.imageUrl
            data.isActive = plan.isActive
            data.isPublic = plan.isPublic
            data.createdAt = js("Date.now()")
            data.updatedAt = js("Date.now()")

            val docRef = (FirebaseFirestore.addDoc(
                FirebaseFirestore.collection(db, "nutritionist_base_plans"),
                data
            ) as Promise<*>).await()

            Result.success(docRef.asDynamic().id as String)
        } catch (e: Exception) {
            console.error("Error creating base plan:", e)
            Result.failure(e)
        }
    }

    override suspend fun updateBasePlan(plan: NutritionistBasePlan): Result<Unit> {
        return try {
            val data = js("({})")
            data.planName = plan.planName
            data.category = plan.category.name
            data.description = plan.description

            val targets = js("({})")
            targets.proteinPerWeek = plan.nutritionalTargets.proteinPerWeek
            targets.caloriesPerDay = plan.nutritionalTargets.caloriesPerDay
            targets.omega3PerWeek = plan.nutritionalTargets.omega3PerWeek
            targets.fatPerDay = plan.nutritionalTargets.fatPerDay
            targets.carbsPerDay = plan.nutritionalTargets.carbsPerDay
            data.nutritionalTargets = targets

            data.targetAudience = plan.targetAudience
            data.goals = plan.goals.toTypedArray()
            data.imageUrl = plan.imageUrl
            data.isActive = plan.isActive
            data.isPublic = plan.isPublic
            data.updatedAt = js("Date.now()")

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "nutritionist_base_plans", plan.basePlanId),
                data
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error updating base plan:", e)
            Result.failure(e)
        }
    }

    override suspend fun deleteBasePlan(planId: String): Result<Unit> {
        return try {
            // Soft delete - mark as inactive
            val data = js("({})")
            data.isActive = false
            data.updatedAt = js("Date.now()")

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "nutritionist_base_plans", planId),
                data
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error deleting base plan:", e)
            Result.failure(e)
        }
    }

    override suspend fun assignPlanToPatient(
        patientId: String,
        basePlanId: String,
        startDate: String,
        deliveryDays: Set<String>,
        deliveryTime: String,
        customNotes: String
    ): Result<String> {
        return try {
            // Create personalized plan record
            val timestamp = js("Date.now()") as Double
            val personalizedPlan = js("({})")
            val personalizedPlanId = "pplan_${timestamp.toLong()}"

            personalizedPlan.patientId = patientId
            personalizedPlan.basePlanId = basePlanId
            personalizedPlan.startDate = startDate
            personalizedPlan.deliveryDays = deliveryDays.toTypedArray()
            personalizedPlan.deliveryTime = deliveryTime

            val customizations = js("({})")
            customizations.modifiedItems = emptyArray<Any>()
            customizations.addedItems = emptyArray<Any>()
            customizations.removedItems = emptyArray<Any>()
            personalizedPlan.customizations = customizations

            personalizedPlan.customNotes = customNotes
            personalizedPlan.status = "pending"  // Awaits user payment confirmation
            personalizedPlan.createdAt = timestamp
            personalizedPlan.assignedAt = timestamp
            personalizedPlan.activatedAt = null

            // Save personalized plan
            (FirebaseFirestore.setDoc(
                FirebaseFirestore.doc(db, "user_personalized_plans", personalizedPlanId),
                personalizedPlan
            ) as Promise<*>).await()

            // Update user document with current plan
            val userUpdate = js("({})")
            userUpdate.currentPlanId = basePlanId
            userUpdate.planStartDate = startDate
            userUpdate.updatedAt = timestamp

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "users", patientId),
                userUpdate
            ) as Promise<*>).await()

            console.log("✅ Successfully assigned plan $basePlanId to patient $patientId")
            console.log("   Personalized plan ID: $personalizedPlanId")


            Result.success(personalizedPlanId)
        } catch (e: Exception) {
            console.error("Error assigning plan:", e)
            Result.failure(e)
        }
    }

    override suspend fun searchProducts(query: String, filters: Map<String, Any>): Result<List<ProductItem>> {
        return try {
            val productsCol = FirebaseFirestore.collection(db, "products")

            // Build query with filters
            var queryRef: dynamic = productsCol
            filters.forEach { (key, value) ->
                when (key) {
                    "category" -> {
                        queryRef = FirebaseFirestore.query(
                            productsCol,
                            FirebaseFirestore.where("category", "==", value)
                        )
                    }
                    "tag" -> {
                        queryRef = FirebaseFirestore.query(
                            productsCol,
                            FirebaseFirestore.where("tags", "array-contains", value)
                        )
                    }
                }
            }

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val products = mutableListOf<ProductItem>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()
                val docId = docDynamic.id as String
                val name = data.name as? String ?: ""

                // Client-side filtering by query
                if (query.isEmpty() || name.contains(query, ignoreCase = true)) {
                    val product = ProductItem(
                        productId = docId,
                        name = name,
                        category = data.category as? String ?: "",
                        tags = (data.tags as? Array<String>)?.toList() ?: emptyList(),
                        protein = (data.protein as? Number)?.toDouble() ?: 0.0,
                        omega3 = (data.omega3 as? Number)?.toDouble() ?: 0.0,
                        calories = (data.calories as? Number)?.toDouble() ?: 0.0,
                        fat = (data.fat as? Number)?.toDouble() ?: 0.0,
                        carbohydrates = (data.carbohydrates as? Number)?.toDouble() ?: 0.0,
                        fiber = (data.fiber as? Number)?.toDouble() ?: 0.0,
                        vitaminD = (data.vitaminD as? Number)?.toDouble() ?: 0.0,
                        vitaminB12 = (data.vitaminB12 as? Number)?.toDouble() ?: 0.0,
                        iron = (data.iron as? Number)?.toDouble() ?: 0.0,
                        price = (data.price as? Number)?.toDouble() ?: 0.0,
                        stockQuantity = (data.stockQuantity as? Number)?.toInt() ?: 0,
                        unit = data.unit as? String ?: "kg",
                        imageUrl = firebase.getProductImageUrl(docId)
                    )
                    products.add(product)
                }
            }

            console.log("✅ Found ${products.size} products")
            Result.success(products)
        } catch (e: Exception) {
            console.error("❌ Error searching products:", e)
            Result.failure(e)
        }
    }

    override suspend fun getProductDetails(productId: String): Result<ProductItem> {
        return try {
            val doc = (FirebaseFirestore.getDoc(
                FirebaseFirestore.doc(db, "products", productId)
            ) as Promise<*>).await()
            val docSnap = doc.asDynamic()
            if (!docSnap.exists()) throw Exception("Product not found")
            val data = docSnap.data()

            val product = ProductItem(
                productId = productId,
                name = data.name as? String ?: "",
                category = data.category as? String ?: "",
                tags = (data.tags as? Array<String>)?.toList() ?: emptyList(),
                protein = (data.protein as? Number)?.toDouble() ?: 0.0,
                omega3 = (data.omega3 as? Number)?.toDouble() ?: 0.0,
                calories = (data.calories as? Number)?.toDouble() ?: 0.0,
                fat = (data.fat as? Number)?.toDouble() ?: 0.0,
                carbohydrates = (data.carbohydrates as? Number)?.toDouble() ?: 0.0,
                fiber = (data.fiber as? Number)?.toDouble() ?: 0.0,
                vitaminD = (data.vitaminD as? Number)?.toDouble() ?: 0.0,
                vitaminB12 = (data.vitaminB12 as? Number)?.toDouble() ?: 0.0,
                iron = (data.iron as? Number)?.toDouble() ?: 0.0,
                price = (data.price as? Number)?.toDouble() ?: 0.0,
                stockQuantity = (data.stockQuantity as? Number)?.toInt() ?: 0,
                unit = data.unit as? String ?: "kg",
                imageUrl = firebase.getProductImageUrl(productId)
            )
            Result.success(product)
        } catch (e: Exception) {
            console.error("❌ Error fetching product details:", e)
            Result.failure(e)
        }
    }


    override suspend fun getConversations(nutritionistId: String): Result<List<Conversation>> {
        return try {
            val queryRef = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "conversations"),
                FirebaseFirestore.where("participants", "array-contains", nutritionistId),
                FirebaseFirestore.orderBy("lastMessageTime", "desc")
            )

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val conversations = mutableListOf<Conversation>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val conversation = Conversation(
                    conversationId = docDynamic.id as String,
                    participants = (data.participants as? Array<String>)?.toList() ?: emptyList(),
                    participantNames = emptyMap(), // Simplified - could parse if needed
                    lastMessage = data.lastMessage as? String ?: "",
                    lastMessageTime = (data.lastMessageTime as? Number)?.toLong() ?: 0,
                    unreadCount = emptyMap(), // Simplified
                    createdAt = (data.createdAt as? Number)?.toLong() ?: 0
                )
                conversations.add(conversation)
            }
            Result.success(conversations)
        } catch (e: Exception) {
            console.error("Error fetching conversations:", e)
            Result.failure(e)
        }
    }

    override suspend fun getMessages(conversationId: String, limit: Int): Result<List<Message>> {
        return try {
            val queryRef = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "messages"),
                FirebaseFirestore.where("conversationId", "==", conversationId),
                FirebaseFirestore.orderBy("timestamp", "asc"), // Use asc for chat history
                FirebaseFirestore.limit(limit)
            )

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val messages = mutableListOf<Message>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val message = Message(
                    messageId = docDynamic.id as String,
                    conversationId = data.conversationId as? String ?: "",
                    senderId = data.senderId as? String ?: "",
                    senderName = data.senderName as? String ?: "",
                    content = data.content as? String ?: "",
                    timestamp = (data.timestamp as? Number)?.toLong() ?: 0,
                    readBy = (data.readBy as? Array<String>)?.toList() ?: emptyList(),
                    messageType = data.messageType as? String ?: "text"
                )
                messages.add(message)
            }
            Result.success(messages)
        } catch (e: Exception) {
            console.error("Error fetching messages:", e)
            Result.failure(e)
        }
    }

    override suspend fun sendMessage(message: Message): Result<String> {
        return try {
            val timestamp = js("Date.now()") as Double
            val messageId = "msg_${timestamp.toLong()}"
            val data = js("({})")

            data.conversationId = message.conversationId
            data.senderId = message.senderId
            data.senderName = message.senderName
            data.content = message.content
            data.timestamp = timestamp
            data.readBy = emptyArray<String>()
            data.messageType = message.messageType

            // 1. Add message
            (FirebaseFirestore.setDoc(
                FirebaseFirestore.doc(db, "messages", messageId),
                data
            ) as Promise<*>).await()

            // 2. Update conversation last message
            val convUpdate = js("({})")
            convUpdate.lastMessage = message.content
            convUpdate.lastMessageTime = timestamp

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "conversations", message.conversationId),
                convUpdate
            ) as Promise<*>).await()

            Result.success(messageId)
        } catch (e: Exception) {
            console.error("Error sending message:", e)
            Result.failure(e)
        }
    }

    override suspend fun markConversationAsRead(conversationId: String, userId: String): Result<Unit> {
        // Implementation for marking read status
        return Result.success(Unit)
    }

    override suspend fun getBiomarkers(patientId: String, biomarkerType: String?): Result<List<Biomarker>> {
        return try {
            var queryRef = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "patient_biomarkers"),
                FirebaseFirestore.where("patientId", "==", patientId)
            )

            if (biomarkerType != null) {
                queryRef = FirebaseFirestore.query(
                    queryRef,
                    FirebaseFirestore.where("biomarkerType", "==", biomarkerType)
                )
            }

            // Order by timestamp descending
            queryRef = FirebaseFirestore.query(queryRef, FirebaseFirestore.orderBy("timestamp", "desc"))

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val biomarkers = mutableListOf<Biomarker>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val biomarker = Biomarker(
                    biomarkerId = docDynamic.id as String,
                    patientId = data.patientId as? String ?: "",
                    nutritionistId = data.nutritionistId as? String ?: "",
                    biomarkerType = data.biomarkerType as? String ?: "glucose",
                    value = (data.value as? Number)?.toDouble() ?: 0.0,
                    unit = data.unit as? String ?: "",
                    notes = data.notes as? String ?: "",
                    isNormal = data.isNormal as? Boolean ?: true,
                    timestamp = (data.timestamp as? Number)?.toLong() ?: 0,
                    createdAt = (data.createdAt as? Number)?.toLong() ?: 0
                )
                biomarkers.add(biomarker)
            }
            Result.success(biomarkers)
        } catch (e: Exception) {
            console.error("Error fetching biomarkers:", e)
            Result.failure(e)
        }
    }

    override suspend fun addBiomarker(biomarker: Biomarker): Result<String> {
        return try {
            val timestamp = js("Date.now()") as Double
            val biomarkerId = "bio_${timestamp.toLong()}"
            val data = js("({})")

            data.patientId = biomarker.patientId
            data.nutritionistId = biomarker.nutritionistId
            data.biomarkerType = biomarker.biomarkerType
            data.value = biomarker.value
            data.unit = biomarker.unit
            data.notes = biomarker.notes
            data.isNormal = biomarker.isNormal
            data.timestamp = if (biomarker.timestamp > 0) biomarker.timestamp else timestamp
            data.createdAt = timestamp

            (FirebaseFirestore.setDoc(
                FirebaseFirestore.doc(db, "patient_biomarkers", biomarkerId),
                data
            ) as Promise<*>).await()

            Result.success(biomarkerId)
        } catch (e: Exception) {
            console.error("Error adding biomarker:", e)
            Result.failure(e)
        }
    }

    override suspend fun updateBiomarker(biomarker: Biomarker): Result<Unit> {
        return try {
            val data = js("({})")
            data.value = biomarker.value
            data.unit = biomarker.unit
            data.notes = biomarker.notes
            data.isNormal = biomarker.isNormal
            data.timestamp = biomarker.timestamp

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "patient_biomarkers", biomarker.biomarkerId),
                data
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error updating biomarker:", e)
            Result.failure(e)
        }
    }

    override suspend fun deleteBiomarker(biomarkerId: String): Result<Unit> {
        return try {
            (FirebaseFirestore.deleteDoc(
                FirebaseFirestore.doc(db, "patient_biomarkers", biomarkerId)
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error deleting biomarker:", e)
            Result.failure(e)
        }
    }

    // Clinical Notes Implementation
    override suspend fun getClinicalNotes(patientId: String, limit: Int): Result<List<ClinicalNote>> {
        return try {
            val queryRef = FirebaseFirestore.query(
                FirebaseFirestore.collection(db, "clinical_notes"),
                FirebaseFirestore.where("patientId", "==", patientId),
                FirebaseFirestore.orderBy("createdAt", "desc"),
                FirebaseFirestore.limit(limit)
            )

            val snapshot = (FirebaseFirestore.getDocs(queryRef) as Promise<*>).await()
            val notes = mutableListOf<ClinicalNote>()
            val docs = snapshot.asDynamic().docs as Array<*>

            docs.forEach { doc ->
                val docDynamic = doc.asDynamic()
                val data = docDynamic.data()

                val note = ClinicalNote(
                    noteId = docDynamic.id as String,
                    patientId = data.patientId as? String ?: "",
                    nutritionistId = data.nutritionistId as? String ?: "",
                    nutritionistName = data.nutritionistName as? String ?: "",
                    noteType = data.noteType as? String ?: "General",
                    content = data.content as? String ?: "",
                    tags = (data.tags as? Array<String>)?.toList() ?: emptyList(),
                    createdAt = (data.createdAt as? Number)?.toLong() ?: 0,
                    updatedAt = (data.updatedAt as? Number)?.toLong() ?: 0
                )
                notes.add(note)
            }
            Result.success(notes)
        } catch (e: Exception) {
            console.error("Error fetching clinical notes:", e)
            Result.failure(e)
        }
    }

    override suspend fun addClinicalNote(note: ClinicalNote): Result<String> {
        return try {
            val timestamp = js("Date.now()") as Double
            val noteId = "note_${timestamp.toLong()}"
            val data = js("({})")

            data.patientId = note.patientId
            data.nutritionistId = note.nutritionistId
            data.nutritionistName = note.nutritionistName
            data.noteType = note.noteType
            data.content = note.content
            data.tags = note.tags.toTypedArray()
            data.createdAt = timestamp
            data.updatedAt = timestamp

            (FirebaseFirestore.setDoc(
                FirebaseFirestore.doc(db, "clinical_notes", noteId),
                data
            ) as Promise<*>).await()

            console.log("✅ Clinical note added with ID: $noteId")
            Result.success(noteId)
        } catch (e: Exception) {
            console.error("Error adding clinical note:", e)
            Result.failure(e)
        }
    }

    override suspend fun updateClinicalNote(note: ClinicalNote): Result<Unit> {
        return try {
            val timestamp = js("Date.now()") as Double
            val data = js("({})")

            data.noteType = note.noteType
            data.content = note.content
            data.tags = note.tags.toTypedArray()
            data.updatedAt = timestamp

            (FirebaseFirestore.updateDoc(
                FirebaseFirestore.doc(db, "clinical_notes", note.noteId),
                data
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error updating clinical note:", e)
            Result.failure(e)
        }
    }

    override suspend fun deleteClinicalNote(noteId: String): Result<Unit> {
        return try {
            (FirebaseFirestore.deleteDoc(
                FirebaseFirestore.doc(db, "clinical_notes", noteId)
            ) as Promise<*>).await()

            Result.success(Unit)
        } catch (e: Exception) {
            console.error("Error deleting clinical note:", e)
            Result.failure(e)
        }
    }
}

